import { Card, CardContent, CardHeader, Typography, Spinner, Stack } from "@altimateai/lego";
import { Transaction } from "@/pages/credits/types";
import dayjs from "dayjs";
import { DataTable } from "@/components/tables";
import { useQuery } from "@tanstack/react-query";
import { getMyUsage } from "@/helpers/apis";
import { useState } from "react";
import { Paginate } from "@/components/paginate";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { DateRange } from "@/components/dateRange";
import { CREDITS_DATE_PREFIX } from "../constants";
import { downloadMyUsageCSV } from "@/helpers/apis";
import { Button } from "@altimateai/lego";
import { DownloadIcon } from "@/assets/icons";
import { formatNumber } from "@/uiCore/components/formatters/format";

export const CreditsUsageHistory = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(30, "day").toDate(),
    dayjs().toDate(),
    CREDITS_DATE_PREFIX
  );

  const { data, isLoading } = useQuery({
    queryKey: ["my-usage", startDate, endDate, page, pageSize],
    queryFn: () =>
      getMyUsage({
        page: page + 1,
        size: pageSize,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      }),
  });

  const headers = [
    {
      label: "AI Task/Teammate",
      key: "teammate",
      uid: "teammate",
      id: 0,
      render: (item: Transaction) => <span>{item.task_name}</span>,
    },
    {
      label: "Date",
      key: "timestamp",
      uid: "timestamp",
      render: (item: Transaction) => (
        <span>{dayjs(item.timestamp).format("DD MMM YYYY, hh:mm A")}</span>
      ),
      id: 1,
    },
    {
      label: "Consumed Credits",
      key: "credits_consumed",
      uid: "credits_consumed",
      render: (item: Transaction) => <span>{formatNumber(item.credits_consumed)}</span>,
      id: 2,
    },
  ];

  const updatePageSize = (size: number) => {
    setPageSize(size);
    setPage(0); // Reset to first page when page size changes
  };

  const handleDownload = () => {
    downloadMyUsageCSV({
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    });
  };

  return (
    <Card>
      <CardHeader>
        <Stack align={"center"} justify={"between"}>
          <Typography variant={"section-heading"}>Credits Usage History</Typography>
          <Stack gap="sm" align="center">
            <Button variant="outline" onClick={handleDownload}>
              <DownloadIcon />
              Download CSV
            </Button>
            <DateRange
              startDate={startDate}
              endDate={endDate}
              onDateRangeSelect={(start, end) => {
                onUpdate(start, end);
              }}
            />
          </Stack>
        </Stack>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Stack justify={"center"} align={"center"} className={"al-min-h-80"} gap={"lg"}>
            <Spinner size={"lg"} />
            <span>Loading...</span>
          </Stack>
        ) : (
          <>
            <DataTable
              header={headers}
              onRowClick={() => {}}
              data={{ items: data?.items ?? [] }}
              resetPage={() => {}}
            />
            <Paginate
              itemCount={data?.total || 0}
              numPages={data?.pages || 0}
              page={page}
              pageSize={pageSize}
              onPageClick={setPage}
              onPageSizeChange={updatePageSize}
              showPageSize
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
